import { setConnectionState } from '@ping/ui';

export interface Subscription {
  unsubscribe(): void;
}

export type ConnState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export interface PingSocket {
  subscribe(onFrame: (data: unknown) => void, onState: (s: ConnState) => void): Subscription;
  /** Send an outbound frame (resume cursors, client_background/foreground
   *  lifecycle). Only the LEADER tab owns the WebSocket, so a follower forwards
   *  the frame to the leader over the BroadcastChannel, which puts it on the
   *  wire. A no-op if no leader has an open socket yet (the caller re-sends on
   *  reconnect). */
  send(data: string | ArrayBuffer): void;
}

interface CreateSocketOptions {
  url: string;
  getToken(): string | null;
  channelName?: string;
  onReconnect?: () => Promise<void>;
}

// Auth: `services/ws-fanout/src/stream.rs` extracts the access token
// via `Query<WsQuery>` (`?token=<jwt>`). Browsers can't set
// `Authorization` on a WebSocket upgrade — query param it is.
//
// Liveness: the server sends WebSocket protocol-level Ping control
// frames every 30s (`services/ws-fanout/src/stream.rs` `ping.tick`).
// The browser auto-replies with Pong at the protocol layer — this is
// the industry-standard keepalive (RFC 6455 §5.5.2) used by Slack,
// WhatsApp, Discord, etc. We deliberately do NOT run an app-level
// ping/pong on top.
//
// Stale-detection watchdog: if we go LIVENESS_WINDOW_MS without
// receiving ANY frame from the server (data, control, anything), the
// underlying transport is *probably* dead — force-close to trigger
// reconnect.
//
// Earlier this window was 90s, which sounds reasonable until you
// notice that the browser's `message` event ONLY fires for
// application data frames (Text / Binary). The server's protocol
// Ping frames keep the TCP alive but are handled at the browser
// layer and never reach our `onmessage` handler. So 90s of
// application silence — which is the normal state for an idle chat
// — kept tripping the watchdog and looking like a flapping
// connection.
//
// Real failure modes (network goes away, server crashes, NAT
// timeout) cause the browser to fire `close` within a minute or two
// on its own via TCP keepalive. We let that handle the common
// case and reserve the watchdog for a much longer fallback. 10
// minutes is what Slack uses for its idle-disconnect timer.
const LIVENESS_WINDOW_MS = 10 * 60_000;
const LIVENESS_CHECK_INTERVAL_MS = 60_000;
const INITIAL_BACKOFF_MS = 250;
const MAX_BACKOFF_MS = 30_000;
const JITTER_MS = 1_000;
const LOCK_WAIT_MS = 50;
// A "stable" connection (>30s connected) gets its backoff counter
// reset, so a transient blip after long uptime starts at 250ms again
// instead of 30s.
const STABLE_CONNECTION_MS = 30_000;
// Backgrounded-tab grace period before we close the socket to save
// power. Slack & WhatsApp Web effectively keep their sockets alive
// indefinitely when the tab is hidden — Chrome throttles background
// timers but not the underlying WebSocket. 30 minutes is a
// conservative middle ground that keeps real-time push working when
// the user switches back from another tab.
const TAB_HIDDEN_CLOSE_MS = 30 * 60_000;

export function createSocket(opts: CreateSocketOptions): PingSocket {
  const { url, getToken, channelName = 'ping-socket-lock', onReconnect } = opts;

  let ws: WebSocket | null = null;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let livenessTimer: ReturnType<typeof setInterval> | null = null;
  let stableConnectionTimer: ReturnType<typeof setTimeout> | null = null;
  let tabHiddenTimer: ReturnType<typeof setTimeout> | null = null;
  let isLeader = false;
  let isActive = false;
  let channel: BroadcastChannel | null = null;
  let isTabVisible = !document.hidden;
  let lastFrameAt = 0;
  // Online state is best-effort — `navigator.onLine` is false-positive-
  // prone (Chrome reports `true` if any network adapter is up) but a
  // hard `false` is reliable, so we use it to short-circuit reconnect
  // attempts that would just spin until DNS comes back.
  let isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

  const frameCallbacks: Array<(data: unknown) => void> = [];
  const stateCallbacks: Array<(s: ConnState) => void> = [];

  function emitState(state: ConnState): void {
    for (const cb of stateCallbacks) {
      cb(state);
    }
    // Surface to the user-facing connectivity banner (`ConnectivityBanner`).
    // Only the LEADER tab owns a real socket; followers receive frames over the
    // BroadcastChannel and would otherwise report their own perpetual
    // 'disconnected' pseudo-state. So the leader is the single source of truth:
    // it pushes to the store AND broadcasts each change so followers mirror it
    // (see the CONN_STATE handler in `setupChannelListeners`).
    if (isLeader) {
      setConnectionState(state);
      channel?.postMessage({ type: 'CONN_STATE', state });
    }
  }

  function emitFrame(data: unknown): void {
    for (const cb of frameCallbacks) {
      cb(data);
    }
  }

  function clearTimers(): void {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (livenessTimer != null) {
      clearInterval(livenessTimer);
      livenessTimer = null;
    }
    if (stableConnectionTimer != null) {
      clearTimeout(stableConnectionTimer);
      stableConnectionTimer = null;
    }
    if (tabHiddenTimer != null) {
      clearTimeout(tabHiddenTimer);
      tabHiddenTimer = null;
    }
  }

  async function scheduleReconnect(): Promise<void> {
    if (!isActive || !isTabVisible || !isOnline) return;

    emitState('reconnecting');
    const backoff = Math.min(INITIAL_BACKOFF_MS * 2 ** reconnectAttempt, MAX_BACKOFF_MS);
    const jitter = Math.random() * JITTER_MS;
    const delay = backoff + jitter;

    reconnectTimer = setTimeout(() => {
      reconnectAttempt++;
      if (onReconnect != null) {
        onReconnect()
          .then(() => {
            connect();
          })
          .catch(() => {
            connect();
          });
      } else {
        connect();
      }
    }, delay);
  }

  // Liveness watchdog. Runs every 15s; if we haven't seen ANY frame
  // from the server within the last 90s, the TCP/WebSocket connection
  // is probably half-open (NAT timeout, dropped Ethernet, suspended
  // VM) and force-closing forces the browser to re-establish. This
  // replaces the broken app-level ping/pong; the server's RFC 6455
  // protocol Ping every 30s + the browser's auto-Pong is the actual
  // keepalive, and the browser hides those frames from us — so we
  // can only observe liveness via inbound traffic.
  function startLivenessWatchdog(): void {
    if (livenessTimer != null) clearInterval(livenessTimer);
    livenessTimer = setInterval(() => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      const age = Date.now() - lastFrameAt;
      if (age > LIVENESS_WINDOW_MS) {
        // Don't emit a state change — the impending `close` handler
        // will do that. Just kill the half-open socket.
        ws.close();
      }
    }, LIVENESS_CHECK_INTERVAL_MS);
  }

  function handleMessage(event: MessageEvent): void {
    // Any frame at all counts as liveness — application data, the
    // server's protocol Ping (invisible to us, but resets a real
    // implementation's read deadline anyway), control frames. We
    // stamp on EVERY message so the watchdog only fires on a truly
    // dead transport.
    lastFrameAt = Date.now();

    // Binary frames carry raw CBOR envelopes from the BE fan-out
    // path; pass straight through to subscribers so PingTransport
    // can deserialize and dispatch to the SDK without an extra hop.
    if (event.data instanceof ArrayBuffer) {
      if (isLeader) {
        channel?.postMessage({ type: 'FRAME', data: event.data });
      }
      emitFrame(event.data);
      return;
    }

    // Text frames are control messages (legacy JSON envelopes).
    if (typeof event.data === 'string') {
      try {
        const data = JSON.parse(event.data);
        if (isLeader) {
          channel?.postMessage({ type: 'FRAME', data });
        }
        emitFrame(data);
      } catch {
        // Unparseable text frame — drop silently. Logging here would
        // spam on every malformed control frame; the BE never sends
        // unknown text shapes under normal operation.
      }
    }
  }

  async function electLeader(): Promise<boolean> {
    const bc = new BroadcastChannel(channelName);
    channel = bc;

    return new Promise((resolve) => {
      let hasResponse = false;

      const listener = (event: MessageEvent): void => {
        if (event.data.type === 'LOCK_RESPONSE') {
          hasResponse = true;
          bc.removeEventListener('message', listener);
          resolve(false);
        }
      };

      bc.addEventListener('message', listener);
      bc.postMessage({ type: 'LOCK_REQUEST' });

      setTimeout(() => {
        bc.removeEventListener('message', listener);
        if (!hasResponse) {
          resolve(true);
        }
      }, LOCK_WAIT_MS);
    });
  }

  function setupChannelListeners(): void {
    if (channel == null) return;

    channel.addEventListener('message', (event: MessageEvent) => {
      if (event.data.type === 'FRAME') {
        if (!isLeader) {
          emitFrame(event.data.data);
        }
      } else if (event.data.type === 'LOCK_REQUEST') {
        if (isLeader) {
          channel?.postMessage({ type: 'LOCK_RESPONSE' });
        }
      } else if (event.data.type === 'LOCK_RELEASE') {
        if (!isLeader) {
          electLeader().then((leader) => {
            if (leader) {
              isLeader = true;
              connect();
            }
          });
        }
      } else if (event.data.type === 'CONN_STATE') {
        // Follower tabs mirror the leader's connection status (see
        // `emitState`) so the banner is correct in every tab.
        if (!isLeader) {
          setConnectionState(event.data.state as ConnState);
        }
      } else if (event.data.type === 'SEND') {
        // A follower asked us (the leader) to put a frame on the wire. Only the
        // leader owns the socket, so this is the single egress point.
        if (isLeader && ws?.readyState === WebSocket.OPEN) {
          ws.send(event.data.data as string | ArrayBuffer);
        }
      }
    });
  }

  // Outbound send. Leader writes directly to its socket; a follower forwards
  // over the BroadcastChannel for the leader to send (ArrayBuffer survives the
  // structured clone). Dropped if no leader socket is open yet — the caller
  // re-sends on reconnect / next tick.
  function send(data: string | ArrayBuffer): void {
    if (isLeader) {
      if (ws?.readyState === WebSocket.OPEN) ws.send(data);
      return;
    }
    channel?.postMessage({ type: 'SEND', data });
  }

  function connect(): void {
    if (!isActive || !isTabVisible) return;

    if (!isLeader) {
      emitState('disconnected');
      return;
    }

    const token = getToken();
    // Skip the upgrade entirely if there's no access token in memory.
    // The transport is constructed during `bootstrapWebSession` which
    // already requires a token, but a sign-out or token-refresh race
    // can drop it mid-flight — without this guard the socket would
    // open a token-less URL that the BE rejects, triggering an
    // immediate close-and-reconnect loop that spams the devtools
    // console. The visibility/leader handlers re-enter `connect()`
    // once a token is available, so this is a soft skip — not a
    // permanent disable.
    if (token == null) {
      emitState('disconnected');
      return;
    }

    emitState('connecting');

    // Token rides in the URL as `?token=<jwt>` — that's the only auth
    // path the Rust ws-fanout reads (Axum `Query<WsQuery>` extractor).
    // Browsers can't set `Authorization` on a WebSocket upgrade, and
    // the Vite dev proxy + nginx prod proxy both forward query strings
    // intact.
    const separator = url.includes('?') ? '&' : '?';
    const authedUrl = `${url}${separator}token=${encodeURIComponent(token)}`;
    ws = new WebSocket(authedUrl);
    // Server sends MLS envelopes as raw CBOR over BinaryMessage frames
    // (services/api/internal/ws/connection.go writePump). Without
    // explicitly opting in to ArrayBuffer the browser hands us a Blob
    // and the PingTransport deserializer chokes; we'd also lose the
    // contentHash dedupe because Blob → Promise<ArrayBuffer> isn't
    // synchronous.
    ws.binaryType = 'arraybuffer';

    ws.addEventListener('open', () => {
      // Don't reset `reconnectAttempt` here — wait until the
      // connection has been stable for >30s. That prevents a fast
      // open→close loop (e.g. server is admitting then immediately
      // dropping us under load) from re-arming the cheap 250ms
      // backoff every cycle, which would hammer the server. After
      // STABLE_CONNECTION_MS the connection is considered "real" and
      // future blips reset the exponential cleanly.
      lastFrameAt = Date.now();
      emitState('connected');
      startLivenessWatchdog();
      if (stableConnectionTimer != null) clearTimeout(stableConnectionTimer);
      stableConnectionTimer = setTimeout(() => {
        reconnectAttempt = 0;
      }, STABLE_CONNECTION_MS);
    });

    ws.addEventListener('message', handleMessage);

    ws.addEventListener('close', () => {
      clearTimers();
      if (isActive && isLeader && isTabVisible && isOnline) {
        void scheduleReconnect();
      } else {
        emitState('disconnected');
      }
    });

    ws.addEventListener('error', () => {
      ws?.close();
    });
  }

  function handleVisibilityChange(): void {
    isTabVisible = !document.hidden;

    if (isTabVisible) {
      if (tabHiddenTimer != null) {
        clearTimeout(tabHiddenTimer);
        tabHiddenTimer = null;
      }

      if (isActive && isLeader && ws?.readyState !== WebSocket.OPEN) {
        reconnectAttempt = 0;
        if (onReconnect != null) {
          onReconnect()
            .then(() => {
              connect();
            })
            .catch(() => {
              connect();
            });
        } else {
          connect();
        }
      }
    } else {
      if (isActive && isLeader && ws?.readyState === WebSocket.OPEN) {
        tabHiddenTimer = setTimeout(() => {
          if (!isTabVisible && ws?.readyState === WebSocket.OPEN) {
            ws.close();
          }
        }, TAB_HIDDEN_CLOSE_MS);
      }
    }
  }

  function handleOnline(): void {
    isOnline = true;
    // Network restore — if the tab is foreground and we don't have a
    // working socket, kick a reconnect now instead of waiting for
    // the watchdog or the next user action. Matches the behaviour
    // Slack/WhatsApp Web have on lid-open.
    if (isActive && isLeader && isTabVisible && ws?.readyState !== WebSocket.OPEN) {
      reconnectAttempt = 0;
      if (reconnectTimer != null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      connect();
    }
  }

  function handleOffline(): void {
    isOnline = false;
    // Don't pre-emptively close — the `close` event will fire when
    // the OS surfaces the disconnect, and our liveness watchdog will
    // catch any half-open socket within 90s. Marking the flag is
    // enough to prevent `scheduleReconnect` from spinning while
    // there's no network.
  }

  function unsubscribe(): void {
    isActive = false;
    clearTimers();

    if (ws != null) {
      ws.close();
      ws = null;
    }

    if (isLeader && channel != null) {
      channel.postMessage({ type: 'LOCK_RELEASE' });
    }

    if (channel != null) {
      channel.close();
      channel = null;
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    }
  }

  function subscribe(
    onFrame: (data: unknown) => void,
    onState: (s: ConnState) => void,
  ): Subscription {
    frameCallbacks.push(onFrame);
    stateCallbacks.push(onState);

    if (!isActive) {
      isActive = true;
      document.addEventListener('visibilitychange', handleVisibilityChange);
      if (typeof window !== 'undefined') {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
      }

      electLeader().then((leader) => {
        isLeader = leader;
        setupChannelListeners();
        if (isLeader && isTabVisible) {
          connect();
        } else {
          emitState('disconnected');
        }
      });
    }

    return { unsubscribe };
  }

  return { subscribe, send };
}
